/**
 * The stone tracery of the churches' pointed windows, after the Oude Kerk's as Janse describes them
 * (De Oude Kerk te Amsterdam, 2004). The side-choir windows of c.1450 are 2.97 m wide, three mullions
 * dividing them into four lights under the traceried arch (section 1.6). The chapel windows of
 * 1485-1516, 5.95 to 6.50 m wide, have a heavy middle post that splits at the springing into two arms
 * of the window arch's radius, with the 'vlieger' between them (section 1.9); each half holds four
 * lights under a row of small arches (the Sint-Joriskapel window as Emanuel de Witte painted it, c.1660).
 * Their rich tracery inside the sub-arches and the vlieger, the cusps, and the iron saddle bars every
 * 90 cm are left out: the forms are not recorded, and the bars would be dark on dark glass. The
 * Nieuwe Kerk's windows of 1538 are not recorded; they take the same scheme, from the same town and
 * decades, at low confidence.
 */
export type TraceryKind = 'mullion' | 'lightHead' | 'fork';

export const TRACERY_KINDS: readonly TraceryKind[] = ['mullion', 'lightHead', 'fork'];

export const TRACERY = {
  /** Light pitch of the side-choir windows: 2.97 m in four lights (Janse, section 1.6). */
  lightPitch: 2.97 / 4,
  maxLights: 8,
  /** A light head's rib, as a share of the light's span; a mullion is two ribs wide, so the ribs of two heads meet on it. */
  lightRib: 0.1,
  /** The fork's arms, as a share of the window's span; the middle post is as wide. Mullion sizes are not recorded. */
  forkRib: 0.05,
  /** How far the stone stands out from the glass (m); the fork and its post stand proud of the light heads they cross. */
  depth: { mullion: 0.18, lightHead: 0.18, fork: 0.2 },
} as const;

/**
 * One piece of tracery in its window's own metres: x across from the left jamb, y up from the sill,
 * z out from the glass. Its unit template (traceryTemplates.ts) is stretched `across`, `up` and `out`
 * and set at (x, y).
 */
export interface TraceryPart {
  readonly kind: TraceryKind;
  readonly x: number;
  readonly y: number;
  readonly across: number;
  readonly up: number;
  readonly out: number;
}

/** Lights in powers of two, as the middle post halves the window, of the pitch nearest the choir windows'. */
export function lightsOf(width: number): number {
  const halvings = Math.round(Math.log2(width / TRACERY.lightPitch));
  return Math.min(TRACERY.maxLights, 2 ** Math.max(0, halvings));
}

function mullions(width: number, rise: number, lights: number): TraceryPart[] {
  const light = width / lights;
  return Array.from({ length: lights - 1 }, (_, k): TraceryPart => {
    const post = k + 1 === lights / 2;
    return {
      kind: 'mullion',
      x: (k + 1) * light,
      y: 0,
      across: post ? TRACERY.forkRib * width : 2 * TRACERY.lightRib * light,
      up: rise,
      out: post ? TRACERY.depth.fork : TRACERY.depth.mullion,
    };
  });
}

/* A two-light window's lights are headed by the fork's arms alone. */
function lightHeads(width: number, rise: number, lights: number): TraceryPart[] {
  if (lights < 4) return [];
  const light = width / lights;
  return Array.from({ length: lights }, (_, k) => ({ kind: 'lightHead', x: k * light, y: rise, across: light, up: light, out: TRACERY.depth.lightHead }));
}

/** The pieces of a pointed window `width` wide whose jambs rise `rise` from sill to springing. */
export function traceryParts(width: number, rise: number): readonly TraceryPart[] {
  const lights = lightsOf(width);
  if (lights < 2) return [];
  const fork: TraceryPart = { kind: 'fork', x: 0, y: rise, across: width, up: width, out: TRACERY.depth.fork };
  return [...mullions(width, rise, lights), ...lightHeads(width, rise, lights), fork];
}
